/**
 * @busmgr/session-expiry
 *
 * Shared session-expiry helpers for Business Manager web apps.
 * Gold standard originated in site_back_office — every auth failure path
 * should call forceSessionExpired() so the user sees a clear "sign in again"
 * message instead of a phantom empty state.
 *
 * Usage:
 *   import { createSessionExpiry, SESSION_REASON } from '@busmgr/session-expiry';
 *   const {
 *     forceSessionExpired,
 *     consumeSessionNotice,
 *     shouldBlockSilentSso,
 *     reasonFromAuthError,
 *     isSessionRedirectInProgress,
 *     isAuthFailure,
 *     attachVisibilityHeartbeat,
 *   } = createSessionExpiry({
 *     storageKeys: ['mm_token', 'mm_refreshToken', 'mm_user'],
 *     loginPath: '/',
 *   });
 *
 * Cookie-first (Phase 3):
 *   REACT_APP_COOKIE_FIRST_AUTH=true|1 — stop writing new JWTs to localStorage;
 *   dual-read of any existing local Bearer remains. Default is off (unchanged).
 *   CRA/Vercel bake at build time — use static process.env.REACT_APP_COOKIE_FIRST_AUTH
 *   only (see isCookieFirstAuthEnabled). Redeploy after changing the Vercel env var.
 */

'use strict';

const SESSION_REASON = Object.freeze({
  EXPIRED: 'session_expired',
  TENANT_MISMATCH: 'tenant_mismatch',
  AUTH_REQUIRED: 'auth_required',
});

const FLAG_KEY = 'busmgr_session_notice';
const OVERLAY_ID = 'busmgr-session-expired-overlay';

/** CRA / Vercel env name for cookie-first stop-write (default off). */
const COOKIE_FIRST_AUTH_ENV = 'REACT_APP_COOKIE_FIRST_AUTH';

/**
 * True when the app should stop writing new JWTs/refresh tokens to localStorage
 * and skip minting via GET /auth/token. Cookie + credentials:'include' is the
 * session source of truth. Dual-read of any existing local Bearer remains.
 *
 * CRA / Vercel bake `REACT_APP_*` at **build** time via webpack DefinePlugin:
 *   - Only **static** `process.env.REACT_APP_COOKIE_FIRST_AUTH` is inlined
 *     (dynamic `process.env[key]` is never replaced — #169 bug).
 *   - After inlining, the expression becomes a string literal (`"true"`).
 *   - Do **not** guard with `typeof process !== 'undefined'`: in the browser
 *     `process` is undefined, so #170 still skipped the assignment and left
 *     the flag off even though the literal `"true"` was in the bundle.
 *
 * @param {NodeJS.ProcessEnv|Record<string,string|undefined>} [env] optional override (tests)
 * @returns {boolean}
 */
function isCookieFirstAuthEnabled(env) {
  let raw;
  if (env && typeof env === 'object' && Object.prototype.hasOwnProperty.call(env, COOKIE_FIRST_AUTH_ENV)) {
    raw = env[COOKIE_FIRST_AUTH_ENV];
  } else {
    // Static member access only — DefinePlugin turns this into a string literal
    // in CRA builds. No typeof-process guard (breaks browser after inlining).
    raw = process.env.REACT_APP_COOKIE_FIRST_AUTH;
  }
  if (raw == null || raw === '') return false;
  const v = String(raw).trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
}

/**
 * Dual-read: return a local Bearer if present (legacy or pre-flag session).
 * Callers still attach Authorization only when this returns a value; otherwise
 * requests rely on the httpOnly busmgr_sso cookie.
 *
 * @param {string} tokenKey
 * @returns {string|null}
 */
function readBearer(tokenKey) {
  if (!tokenKey) return null;
  try {
    return localStorage.getItem(tokenKey) || null;
  } catch (_) {
    return null;
  }
}

/**
 * Persist JWT (+ optional refresh). No-op when cookie-first is enabled.
 * Logout / forceSessionExpired still clear keys via clearLocalSession.
 *
 * @param {object} options
 * @param {string} options.tokenKey
 * @param {string} [options.refreshKey]
 * @param {string} [options.token]
 * @param {string} [options.refreshToken]
 * @returns {boolean} true if writes were attempted (flag off)
 */
function persistSessionTokens(options = {}) {
  if (isCookieFirstAuthEnabled()) return false;
  const tokenKey = options.tokenKey;
  const refreshKey = options.refreshKey;
  const token = options.token;
  const refreshToken = options.refreshToken;
  try {
    if (tokenKey && token) localStorage.setItem(tokenKey, token);
    if (refreshKey && refreshToken) localStorage.setItem(refreshKey, refreshToken);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Cache user JSON for UI bootstrap. Allowed under cookie-first (not a JWT).
 *
 * @param {object} options
 * @param {string} options.userKey
 * @param {object|string} options.user
 * @returns {boolean}
 */
function persistSessionUser(options = {}) {
  const userKey = options.userKey;
  const user = options.user;
  if (!userKey || user == null) return false;
  try {
    localStorage.setItem(userKey, typeof user === 'string' ? user : JSON.stringify(user));
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Whether checkSSOSession should call GET /auth/token and store a local JWT.
 * False under cookie-first (cookie-only session).
 *
 * @returns {boolean}
 */
function shouldMintLocalBearer() {
  return !isCookieFirstAuthEnabled();
}

/**
 * @param {object} [options]
 * @param {string[]} [options.storageKeys] localStorage keys to clear on expiry
 * @param {string} [options.loginPath] pathname to redirect to (e.g. '/', '/login', '/admin')
 * @returns {object} session expiry helpers bound to the given keys/path
 */
function createSessionExpiry(options = {}) {
  const storageKeys = Array.isArray(options.storageKeys) && options.storageKeys.length
    ? options.storageKeys
    : ['token', 'refreshToken', 'user'];
  const loginPath = options.loginPath || '/';

  let _redirecting = false;

  function isSessionRedirectInProgress() {
    return _redirecting;
  }

  function isAuthFailure(err) {
    return err?.response?.status === 401 || err?.status === 401;
  }

  /**
   * Classify an API 401 body into a login-banner reason.
   */
  function reasonFromAuthError(err) {
    const msg = String(err?.response?.data?.message || err?.message || '').toLowerCase();
    if (msg.includes('does not match this tenant') || msg.includes('tenant')) {
      return SESSION_REASON.TENANT_MISMATCH;
    }
    if (msg.includes('no token') || msg.includes('authentication required')) {
      return SESSION_REASON.AUTH_REQUIRED;
    }
    return SESSION_REASON.EXPIRED;
  }

  function clearLocalSession() {
    try {
      for (const key of storageKeys) {
        localStorage.removeItem(key);
      }
    } catch (_) {
      /* ignore */
    }
  }

  function showBlockingOverlay(message) {
    if (typeof document === 'undefined') return;
    if (document.getElementById(OVERLAY_ID)) return;
    const el = document.createElement('div');
    el.id = OVERLAY_ID;
    el.setAttribute('role', 'alert');
    el.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:99999',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'background:rgba(15,23,42,0.72)',
      'padding:24px',
    ].join(';');
    el.innerHTML = `
    <div style="background:#fff;border-radius:12px;max-width:420px;width:100%;padding:20px 22px;
                box-shadow:0 20px 50px rgba(0,0,0,0.35);font-family:system-ui,sans-serif">
      <div style="font-weight:700;font-size:16px;color:#92400e;margin-bottom:8px">Sign in required</div>
      <div style="font-size:14px;color:#374151;line-height:1.45">${message}</div>
      <div style="font-size:12px;color:#6b7280;margin-top:12px">Redirecting to the login screen…</div>
    </div>
  `;
    document.body.appendChild(el);
  }

  /**
   * Clear credentials, show a blocking notice, and hard-redirect to login.
   * Safe to call multiple times — subsequent calls no-op.
   */
  function forceSessionExpired(reason = SESSION_REASON.EXPIRED, detailMessage) {
    if (_redirecting) return;
    _redirecting = true;

    const reasonKey = reason || SESSION_REASON.EXPIRED;
    const message =
      detailMessage ||
      (reasonKey === SESSION_REASON.TENANT_MISMATCH
        ? 'Your session does not match this workspace. Please sign in again.'
        : reasonKey === SESSION_REASON.AUTH_REQUIRED
          ? 'You need to sign in to continue.'
          : 'Your session has expired. Please sign in again.');

    clearLocalSession();

    try {
      sessionStorage.setItem(
        FLAG_KEY,
        JSON.stringify({ reason: reasonKey, message, at: Date.now() })
      );
    } catch (_) {
      /* ignore */
    }

    showBlockingOverlay(message);

    const url = new URL(window.location.href);
    url.pathname = loginPath;
    url.search = '';
    url.hash = '';
    url.searchParams.set('reason', reasonKey);

    // Allow the overlay to paint, then navigate.
    window.setTimeout(() => {
      window.location.replace(url.toString());
    }, 50);
  }

  /**
   * Read + clear the one-shot notice written before redirect.
   */
  function consumeSessionNotice() {
    try {
      const raw = sessionStorage.getItem(FLAG_KEY);
      if (raw) sessionStorage.removeItem(FLAG_KEY);
      const params = new URLSearchParams(window.location.search);
      const reason = params.get('reason');
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          reason: parsed.reason || reason || SESSION_REASON.EXPIRED,
          message: parsed.message || null,
        };
      }
      if (reason) {
        return { reason, message: null };
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  }

  /**
   * True when this page load was an intentional forced logout —
   * App bootstrap must NOT silently revive the session via SSO cookie.
   */
  function shouldBlockSilentSso() {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (
      reason === SESSION_REASON.EXPIRED ||
      reason === SESSION_REASON.TENANT_MISMATCH ||
      reason === SESSION_REASON.AUTH_REQUIRED
    ) {
      return true;
    }
    try {
      return Boolean(sessionStorage.getItem(FLAG_KEY));
    } catch (_) {
      return false;
    }
  }

  /**
   * After a local Bearer `/auth/me` 401: clear this app's localStorage keys
   * only, then retry once with cookie-only SSO (`adoptSession` /
   * `checkSSOSession`). Success stays signed in and does **not** set
   * `shouldBlockSilentSso`. Failure falls through to `forceSessionExpired`.
   *
   * Callers must still treat tenant mismatch as a hard stop after a
   * successful adopt — do not silent-adopt another workspace.
   *
   * @param {object} options
   * @param {() => Promise<unknown>} [options.adoptSession] cookie-only SSO mint
   * @param {object} [options.err] original 401 error (for reason/message)
   * @returns {Promise<unknown|null>} adopted user/session, or null after forced expiry
   */
  async function recoverFromStaleBearer(options = {}) {
    const adoptSession = options.adoptSession;
    const err = options.err;

    clearLocalSession();

    if (typeof adoptSession === 'function' && !_redirecting) {
      try {
        const adopted = await adoptSession();
        if (adopted) return adopted;
      } catch (_) {
        /* cookie retry failed — fall through to forced expiry */
      }
    }

    forceSessionExpired(
      reasonFromAuthError(err),
      err?.response?.data?.message || err?.message
    );
    return null;
  }

  /**
   * Re-validate (or re-adopt) the session when the tab becomes visible.
   * Cookie-only SSO sessions have no localStorage JWT yet — missing token
   * must try adoptSession() instead of treating the user as logged out.
   * Stale local Bearer 401 → clear local keys and cookie-retry once before
   * forcing expiry (same contract as bootstrap recoverFromStaleBearer).
   *
   * @param {object} options
   * @param {string} [options.tokenKey] localStorage key for the Bearer JWT
   * @param {() => Promise<unknown>} [options.revalidate] GET /auth/me (or equivalent)
   * @param {() => Promise<unknown>} [options.adoptSession] checkSSOSession() — mint JWT from cookie
   * @returns {() => void} detach listener
   */
  function attachVisibilityHeartbeat(options = {}) {
    const tokenKey = options.tokenKey || storageKeys[0];
    const revalidate = options.revalidate;
    const adoptSession = options.adoptSession;

    async function onVisible() {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      if (_redirecting) return;

      const token = readBearer(tokenKey);

      try {
        if (token) {
          if (typeof revalidate === 'function') {
            await revalidate();
          }
          return;
        }
        if (typeof adoptSession === 'function') {
          const adopted = await adoptSession();
          if (adopted) return;
        }
        forceSessionExpired(SESSION_REASON.AUTH_REQUIRED);
      } catch (err) {
        if (isAuthFailure(err)) {
          await recoverFromStaleBearer({ adoptSession, err });
        }
      }
    }

    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
      return function detach() {};
    }
    document.addEventListener('visibilitychange', onVisible);
    return function detach() {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }

  return {
    SESSION_REASON,
    forceSessionExpired,
    consumeSessionNotice,
    shouldBlockSilentSso,
    reasonFromAuthError,
    isSessionRedirectInProgress,
    isAuthFailure,
    clearLocalSession,
    recoverFromStaleBearer,
    attachVisibilityHeartbeat,
  };
}

module.exports = {
  __esModule: true,
  SESSION_REASON,
  COOKIE_FIRST_AUTH_ENV,
  isCookieFirstAuthEnabled,
  readBearer,
  persistSessionTokens,
  persistSessionUser,
  shouldMintLocalBearer,
  createSessionExpiry,
};
